import { GRAVITY } from './constants.js';
import { PARTS } from './parts.js';
import { Hip } from './hip.js';
import { Torso } from './torso.js';
import { Limb } from './limbs.js';
import { Quaternion } from './quaternion.js';
import { Vector3 } from '../util/vector-utils.js';
import { roundFloat } from '../util/math-util.js';
import { LEFT_SIDE, RIGHT_SIDE, } from '../types';
import { SensorMap, } from '../types';
import { log, FACILITY as LOG_FACILITY, format } from '../util/log';
import { Alignment, AlignmentConfig } from './alignment';
import { translateKeys } from '../util/keypoints';
import { FORCE_INDEX, MOMENT_INDEX } from './analytics-tuple';
var LOG_BODY = LOG_FACILITY.BODY;
function CmFromParts(parts, totalMass, kchain) {
    return parts.reduce(function (acc, part) {
        acc.x += (kchain[part].pCenterMass.x * kchain[part].mass) / totalMass;
        acc.y += (kchain[part].pCenterMass.y * kchain[part].mass) / totalMass;
        acc.z += (kchain[part].pCenterMass.z * kchain[part].mass) / totalMass;
        return acc;
    }, new Vector3());
}
function getMoment(origin, forceLocation, force) {
    return forceLocation.subtract(origin).cross(force)
        .round(3);
}
var UpperBody = (function () {
    function UpperBody(kchain, fullBodyMass) {
        var _this = this;
        this.kchain = kchain;
        this.parts = [
            'torso',
            'right-upper-arm',
            'right-lower-arm',
            'left-upper-arm',
            'left-lower-arm',
        ];
        this.mass = this.parts.reduce(function (acc, part) { return (acc += _this.kchain[part].mass); }, 0);
        this.gravityForce = new Vector3(0, 0, (GRAVITY * this.mass) / fullBodyMass);
    }
    UpperBody.prototype.getCm = function () {
        return CmFromParts(this.parts, this.mass, this.kchain);
    };
    return UpperBody;
}());
var sideMap = [
    'right',
    'right',
    'left',
    'left',
    'middle',
    'right',
    'right',
    'left',
    'left',
    'middle',
];
export function getSide(idx) {
    return sideMap[idx];
}
var Body = (function () {
    function Body() {
        var _this = this;
        this.setInitialPositions = function () {
            _this.update(undefined);
        };
        this.alignment = new Alignment(this);
        this.partMap = Object.keys(SensorMap).filter(function (item) {
            return isNaN(Number(item));
        });
        this.leftLegLength = 0;
        this.rightLegLength = 0;
        this.gravityForce = new Vector3(0, 0, GRAVITY);
        this.kchain = {};
        this.kchain['hip'] = new Hip(PARTS['hip']);
        this.kchain['right-upper-leg'] = new Limb(PARTS['right-upper-leg']);
        this.kchain['right-lower-leg'] = new Limb(PARTS['right-lower-leg']);
        this.kchain['left-upper-leg'] = new Limb(PARTS['left-upper-leg']);
        this.kchain['left-lower-leg'] = new Limb(PARTS['left-lower-leg']);
        this.kchain['torso'] = new Torso(PARTS['torso']);
        this.kchain['left-upper-arm'] = new Limb(PARTS['left-upper-arm']);
        this.kchain['left-lower-arm'] = new Limb(PARTS['left-lower-arm']);
        this.kchain['right-upper-arm'] = new Limb(PARTS['right-upper-arm']);
        this.kchain['right-lower-arm'] = new Limb(PARTS['right-lower-arm']);
        this.kchain['hip'].addChildren([
            this.kchain['right-upper-leg'],
            this.kchain['left-upper-leg'],
            this.kchain['torso'],
        ]);
        this.kchain['torso'].addChildren([
            this.kchain['right-upper-arm'],
            this.kchain['left-upper-arm'],
        ]);
        this.kchain['right-upper-arm'].addChild(this.kchain['right-lower-arm']);
        this.kchain['left-upper-arm'].addChild(this.kchain['left-lower-arm']);
        this.kchain['right-upper-leg'].addChild(this.kchain['right-lower-leg']);
        this.kchain['left-upper-leg'].addChild(this.kchain['left-lower-leg']);
        this.mass = this.partMap.reduce(function (acc, part) { return (acc += _this.kchain[part].mass); }, 0);
        this.upperBody = new UpperBody(this.kchain, this.mass);
        this.fullBody = {
            getCm: function () { return CmFromParts(_this.partMap, _this.mass, _this.kchain); },
        };
        log.info(LOG_BODY, 'Body constructor');
        this.update(undefined);
    }
    Body.prototype.peekLegHeight = function (side) {
        return (-1 *
            (this.kchain[side + "-upper-leg"].peekZ() +
                this.kchain[side + "-lower-leg"].peekZ()));
    };
    Body.prototype.getPart = function (index) {
        var name = this.partMap[index];
        return this.kchain[name];
    };
    Body.prototype.getJoint = function (parent, child) {
        return this.kchain[parent].joints[child];
    };
    Body.prototype.updateFromKeypoints = function (keypoints) {
        var _this = this;
        if (keypoints === void 0) { keypoints = undefined; }
        if (keypoints === undefined) {
            return false;
        }
        this.partMap.forEach(function (part) {
            return _this.kchain[part].updateFromKeypoints(translateKeys(part, keypoints));
        });
        this.leftLegLength = this.peekLegHeight('left');
        this.rightLegLength = this.peekLegHeight('right');
        this.kchain['hip'].update(Math.max(this.leftLegLength, this.rightLegLength));
        return true;
    };
    Body.prototype.update = function (frame) {
        var _this = this;
        if (frame && frame.readings.length === 10) {
            frame.readings.forEach(function (reading, idx) {
                var part = _this.getPart(idx);
                var acceleration = frame.accelerations
                    ? frame.accelerations[idx]
                    : null;
                var angularVelocity = frame.angularVelocities
                    ? frame.angularVelocities[idx]
                    : null;
                part.reading(frame.timestamp, reading, acceleration, angularVelocity);
                part.updateModel();
            });
        }
        else {
            this.partMap.forEach(function (part) { return _this.kchain[part].updateModel(); });
        }
        this.leftLegLength = this.peekLegHeight('left');
        this.rightLegLength = this.peekLegHeight('right');
        this.kchain['hip'].update(Math.max(this.leftLegLength, this.rightLegLength));
        if (this.alignment.getInProcess()) {
            this.alignment.push(frame);
            this.alignment.update();
        }
    };
    Body.prototype.getQInitials = function () {
        log.debug(LOG_BODY, 'getQInitials');
        var readings = [];
        for (var idx = 0; idx < 10; ++idx) {
            readings.push(this.getPart(idx).getInitialRotation());
        }
        return readings;
    };
    Body.prototype.getAlignmentConfig = function () {
        var result = new AlignmentConfig();
        result.frames = this.alignment.config.frames;
        result.qInitials = this.getQInitials();
        result.axisTransforms = this.getAxisTransforms();
        return result;
    };
    Body.prototype.setAlignment = function (rotations, transforms) {
        log.info(LOG_BODY, 'setAlignment', format(rotations), format(transforms));
        if (!rotations) {
            log.error(LOG_BODY, 'Body.setAlignment no frame specified: using current sensors');
            rotations = this.getQSensors();
        }
        for (var index = 0; index < 10; ++index) {
            this.getPart(index).setAlignment(rotations[index], transforms ? transforms[index] : null);
        }
    };
    Body.prototype.clearQInitials = function () {
        log.info(LOG_BODY, 'clearQInitials');
        for (var idx = 0; idx < 10; ++idx) {
            this.getPart(idx).setInitialRotation(Quaternion.ONE.toVector());
        }
    };
    Body.prototype.getQSensors = function () {
        log.info(LOG_BODY, 'getQSensors');
        var readings = [];
        for (var idx = 0; idx < 10; ++idx) {
            readings.push(this.getPart(idx).qSensor.toVector());
        }
        return readings;
    };
    Body.prototype.getAxisTransforms = function () {
        var _this = this;
        return this.partMap.map(function (part) {
            return _this.kchain[part].qAxisTransform.toVector();
        });
    };
    Body.prototype.setAxisTransforms = function (transforms) {
        var _this = this;
        if (!transforms) {
            return;
        }
        transforms.forEach(function (transform, idx) {
            _this.getPart(idx).qAxisTransform = new Quaternion(transform);
        });
    };
    Body.prototype.clearAxisTransforms = function () {
        var _this = this;
        this.partMap.forEach(function (part) { return _this.kchain[part].clearAxisTransform(); });
    };
    Body.prototype.getSacral = function () {
        var force = new Vector3(this.upperBody.gravityForce).round(3);
        var moment = getMoment(this.kchain['hip'].pCenter, this.upperBody.getCm(), this.upperBody.gravityForce);
        var joint = this.getJoint('hip', 'torso');
        var momentsAnatomical = joint.getAnatomicalMoments(moment);
        var anglesAnatomical = joint.getAnatomicalAngles();
        return [force, moment, momentsAnatomical, anglesAnatomical];
    };
    Body.prototype.getHip = function (grfDistribution, sacralMoment) {
        var _this = this;
        var joint = ['left', 'right'].map(function (side) {
            return _this.getJoint('hip', side + "-upper-leg");
        });
        var forces = [
            new Vector3(this.upperBody.gravityForce).scale(grfDistribution)
                .round(3),
            new Vector3(this.upperBody.gravityForce)
                .scale(1 - grfDistribution)
                .round(3),
        ];
        var moments = [
            new Vector3(sacralMoment).scale(grfDistribution)
                .round(3),
            new Vector3(sacralMoment).scale(1 - grfDistribution)
                .round(3),
        ];
        var momentAnatomical = [
            joint[LEFT_SIDE].getAnatomicalMoments(moments[LEFT_SIDE]),
            joint[RIGHT_SIDE].getAnatomicalMoments(moments[RIGHT_SIDE]),
        ];
        var anglesAnatomical = [
            joint[LEFT_SIDE].getAnatomicalAngles(),
            joint[RIGHT_SIDE].getAnatomicalAngles(),
        ];
        return [forces, moments, momentAnatomical, anglesAnatomical];
    };
    Body.prototype.getKnee = function (hipsAnalytic) {
        var _this = this;
        var knee = ['left', 'right'].map(function (side) {
            return _this.getJoint(side + "-upper-leg", side + "-lower-leg");
        });
        var hip = ['left', 'right'].map(function (side) {
            return _this.getJoint('hip', side + "-upper-leg");
        });
        var forces = [LEFT_SIDE, RIGHT_SIDE].map(function (side) {
            return new Vector3(hipsAnalytic[FORCE_INDEX][side]).round(3);
        });
        var moments = [LEFT_SIDE, RIGHT_SIDE].map(function (side) {
            var moment = new Vector3(hipsAnalytic[MOMENT_INDEX][side]);
            moment.add(getMoment(knee[side].position, hip[side].position, hipsAnalytic[FORCE_INDEX][side]));
            return moment.round(3);
        });
        var momentsAnatomical = [LEFT_SIDE, RIGHT_SIDE].map(function (side) {
            var momentAnatomical = new Vector3(knee[side].getAnatomicalMoments(moments[side]));
            return momentAnatomical.round(3).toArray();
        });
        var anglesAnatomical = [LEFT_SIDE, RIGHT_SIDE].map(function (side) {
            return knee[side].getAnatomicalAngles();
        });
        var result = [forces, moments, momentsAnatomical, anglesAnatomical];
        return result;
    };
    Body.prototype.getGroundReactionForce = function () {
        var fullBodyCoM = this.fullBody.getCm();
        var leftFoot = new Vector3(this.kchain['left-lower-leg'].distal);
        leftFoot.z = 0;
        var rightFoot = new Vector3(this.kchain['right-lower-leg'].distal);
        rightFoot.z = 0;
        var CoM = new Vector3(fullBodyCoM);
        CoM.z = 0;
        var stanceVector = rightFoot.subtract(leftFoot);
        var CoMVector = CoM.subtract(leftFoot);
        var stanceDistance = stanceVector.norm();
        var CoMProjection = stanceVector.scalarProjection(CoMVector);
        var stanceDistribution = roundFloat(CoMProjection / stanceDistance, 5);
        var legLengthDistribution = roundFloat((this.rightLegLength - this.leftLegLength) /
            Math.max(this.rightLegLength, this.leftLegLength), 5);
        var contactFactor = {
            right: 1,
            left: 1,
        };
        if (legLengthDistribution >= 0) {
            if (legLengthDistribution > 0.2) {
                contactFactor.right = 1;
                contactFactor.left = 0;
                stanceDistribution = 1;
            }
            else if (legLengthDistribution > 0.12) {
                contactFactor.right = 0.8;
                contactFactor.left = 0.2;
            }
        }
        else {
            if (legLengthDistribution < -0.2) {
                contactFactor.right = 0;
                contactFactor.left = 1;
                stanceDistribution = 0;
            }
            else if (legLengthDistribution < -0.12) {
                contactFactor.right = 0.8;
                contactFactor.left = 0.2;
            }
        }
        var magnitude = [
            GRAVITY * contactFactor.left * (1 - stanceDistribution),
            GRAVITY * contactFactor.right * stanceDistribution,
        ];
        if (magnitude[0] > GRAVITY || magnitude[1] > GRAVITY) {
            log.info(LOG_BODY, 'Stance calculation yields more than 100%', magnitude, contactFactor, stanceDistribution);
            magnitude[0] = magnitude[0] > GRAVITY ? GRAVITY : magnitude[0];
            magnitude[1] = magnitude[1] > GRAVITY ? GRAVITY : magnitude[1];
        }
        var result = [
            [
                fullBodyCoM
                    .subtract(this.kchain['left-lower-leg'].distal)
                    .normalize()
                    .scale(magnitude[0] / GRAVITY)
                    .round(3),
                fullBodyCoM
                    .subtract(this.kchain['right-lower-leg'].distal)
                    .normalize()
                    .scale(magnitude[1] / GRAVITY)
                    .round(3),
            ],
        ];
        return result;
    };
    Body.prototype.getGroundReactionForceAccel = function () {
        var leftLeg = this.kchain['left-lower-leg'];
        var rightLeg = this.kchain['right-lower-leg'];
        var THRESHOLD_M = 0.005;
        if (leftLeg.distal.z < THRESHOLD_M && rightLeg.distal.z < THRESHOLD_M) {
            var CoM = new Vector3(this.fullBody.getCm());
            var leftFoot = new Vector3(leftLeg.distal);
            var rightFoot = new Vector3(rightLeg.distal);
            CoM.z = 0;
            leftFoot.z = 0;
            rightFoot.z = 0;
            var stanceVector = rightFoot.subtract(leftFoot);
            var CoMVector = CoM.subtract(leftFoot);
            var stanceDistance = stanceVector.norm();
            var CoMProjection = stanceVector.scalarProjection(CoMVector);
            var stanceDistribution = CoMProjection / stanceDistance;
            var result_1 = [[
                    leftLeg.accelModel
                        .round(3)
                        .scale((1 - stanceDistribution)),
                    rightLeg.accelModel
                        .round(3)
                        .scale((stanceDistribution)),
                ]];
            return result_1;
        }
        var result = [
            [leftLeg, rightLeg].map(function (part) {
                if (part.distal.z >= THRESHOLD_M) {
                    return new Vector3(0, 0, 0);
                }
                return part.accelModel.round(3);
            }),
        ];
        return result;
    };
    return Body;
}());
export { Body };
//# sourceMappingURL=body.js.map